/**
 * Boost Search Yardage Price Fix
 * Converts Boost search result prices to per-yard for Fractional Yardage tagged products
 */

(function() {
  'use strict';

  const CONFIG = {
    yardsToUnitsMultiplier: 2, // 1 yard = 2 units, so price * 2 = per-yard price
  };

  // Add CSS to hide Boost search prices until converted
  const style = document.createElement('style');
  style.textContent = `
    /* Hide Boost instant search prices initially */
    .boost-sd__instant-search-container [class*="price"]:not([data-yardage-converted]):not([data-yardage-checked]) {
      opacity: 0 !important;
    }

    /* Smooth fade-in after conversion */
    .boost-sd__instant-search-container [class*="price"][data-yardage-converted],
    .boost-sd__instant-search-container [class*="price"][data-yardage-checked] {
      opacity: 1 !important;
      transition: opacity 0.2s ease-in;
    }
  `;
  document.head.appendChild(style);

  // Store product handles with Fractional Yardage tag (cached)
  const fractionalYardageProducts = new Set();
  const nonFractionalProducts = new Set(); // Cache products that DON'T have the tag
  const pendingFetches = new Map(); // Prevent duplicate fetches

  /**
   * Check if a product handle has the Fractional Yardage tag
   * Uses aggressive caching to minimize delays
   */
  async function checkProductTags(handle) {
    // Check cache first
    if (fractionalYardageProducts.has(handle)) {
      return true;
    }
    if (nonFractionalProducts.has(handle)) {
      return false;
    }

    // Check if we're already fetching this product
    if (pendingFetches.has(handle)) {
      return await pendingFetches.get(handle);
    }

    // Fetch product data
    const fetchPromise = (async () => {
      try {
        const response = await fetch(`/products/${handle}.js`);
        const product = await response.json();

        // Check if product has "Fractional Yardage" tag
        const hasFractionalYardage = product.tags.some(tag =>
          tag.toLowerCase() === 'fractional yardage'
        );

        if (hasFractionalYardage) {
          fractionalYardageProducts.add(handle);
          return true;
        } else {
          nonFractionalProducts.add(handle);
          return false;
        }
      } catch (error) {
        console.error('[Boost Yardage Search] Error checking product tags:', error);
        nonFractionalProducts.add(handle); // Cache as non-fractional to avoid retry
        return false;
      } finally {
        pendingFetches.delete(handle); // Clean up
      }
    })();

    pendingFetches.set(handle, fetchPromise);
    return await fetchPromise;
  }

  /**
   * Extract product handle from Boost product card
   */
  function getProductHandle(productCard) {
    // Try to find product link - check multiple patterns
    let productLink = productCard.querySelector('a[href*="/products/"]');

    // If productCard itself is a link, use it
    if (!productLink && productCard.tagName === 'A' && productCard.href && productCard.href.includes('/products/')) {
      productLink = productCard;
    }

    // Try finding link in children
    if (!productLink) {
      productLink = productCard.querySelector('[href*="/products/"]');
    }

    if (!productLink) {
      console.log('[Boost Yardage Search] No product link found in:', productCard);
      return null;
    }

    const url = productLink.getAttribute('href') || productLink.href;
    const match = url.match(/\/products\/([^?#\/]+)/);
    return match ? match[1] : null;
  }

  /**
   * Convert a product card's price to per-yard
   */
  async function convertProductPrice(productCard) {
    if (productCard.hasAttribute('data-yardage-converted')) {
      return; // Already converted
    }

    const handle = getProductHandle(productCard);
    if (!handle) {
      console.log('[Boost Yardage Search] No handle found for product card');
      return;
    }

    // Find price elements - try multiple selectors
    let priceElements = productCard.querySelectorAll('.boost-sd__suggestion-queries-item-price, .boost-sd__format-currency, [class*="price"]');

    // Filter to only elements that contain a dollar sign
    priceElements = Array.from(priceElements).filter(el => el.textContent.includes('$'));

    // Check if this product has Fractional Yardage tag
    const hasFractionalYardage = await checkProductTags(handle);
    if (!hasFractionalYardage) {
      console.log('[Boost Yardage Search] Product', handle, 'does not have Fractional Yardage tag');
      // Mark as checked so price shows (not fractional, no conversion needed)
      priceElements.forEach(el => el.setAttribute('data-yardage-checked', 'true'));
      return;
    }

    console.log('[Boost Yardage Search] Converting price for', handle);

    priceElements.forEach(priceElement => {
      if (priceElement.hasAttribute('data-yardage-converted')) {
        return; // Already converted
      }

      const priceText = priceElement.textContent.trim();

      // Extract numeric price (handle formats like "$3.98" or "3.98")
      const priceMatch = priceText.match(/[\d,]+\.?\d*/);
      if (!priceMatch) return;

      const unitPrice = parseFloat(priceMatch[0].replace(/,/g, ''));
      const yardPrice = unitPrice * CONFIG.yardsToUnitsMultiplier;

      // Update the price text
      priceElement.textContent = priceElement.textContent.replace(
        priceMatch[0],
        yardPrice.toFixed(2)
      );

      // Add "/yard" suffix if not already there
      if (!priceElement.textContent.includes('/yard')) {
        priceElement.textContent += '/yard';
      }

      priceElement.setAttribute('data-yardage-converted', 'true');
      console.log('[Boost Yardage Search] Converted', handle, 'from', `$${unitPrice}`, 'to', `$${yardPrice}/yard`);
    });

    productCard.setAttribute('data-yardage-converted', 'true');
  }

  /**
   * Pre-fetch product tags for all visible products (speeds up conversion)
   */
  function prefetchProductTags() {
    // Find all product links in Boost containers
    const instantSearchContainers = document.querySelectorAll('.boost-sd__instant-search-container, .boost-sd__suggestion-wrapper');
    const productLinks = new Set();

    instantSearchContainers.forEach(container => {
      const links = container.querySelectorAll('[href*="/products/"]');
      links.forEach(link => {
        const url = link.getAttribute('href') || link.href;
        const match = url.match(/\/products\/([^?#\/]+)/);
        if (match) productLinks.add(match[1]);
      });
    });

    productLinks.forEach(handle => {
      if (!fractionalYardageProducts.has(handle) && !nonFractionalProducts.has(handle)) {
        // Start fetching in background (don't await)
        checkProductTags(handle);
      }
    });
  }

  /**
   * Convert all visible Boost search result products
   */
  async function convertBoostSearchResults() {
    // Look for Boost instant search containers
    const instantSearchContainers = document.querySelectorAll('.boost-sd__instant-search-container, .boost-sd__suggestion-wrapper');

    // Find all elements with product links inside Boost containers
    let productCards = [];

    if (instantSearchContainers.length > 0) {
      // Instant search: find any element containing a product link
      instantSearchContainers.forEach(container => {
        const items = container.querySelectorAll('[href*="/products/"]');
        items.forEach(item => {
          // Get the parent container (usually 2-3 levels up)
          let parent = item.closest('[class*="item"], [class*="product"], [class*="result"]') || item.parentElement;
          if (parent && !productCards.includes(parent)) {
            productCards.push(parent);
          }
        });
      });
    }

    // Fallback: try original selectors for full search page
    if (productCards.length === 0) {
      productCards = Array.from(document.querySelectorAll('.boost-sd__suggestion-queries-item-product, .boost-sd__product-item, [class*="boost-sd__product"]'));
    }

    if (productCards.length === 0) {
      console.log('[Boost Yardage Search] No Boost product cards found');
      return;
    }

    console.log('[Boost Yardage Search] Found', productCards.length, 'product cards');

    // Pre-fetch all product tags in parallel for faster conversion
    prefetchProductTags();

    // Convert each product card
    for (const card of productCards) {
      await convertProductPrice(card);
    }
  }

  /**
   * Watch for Boost search results being added/updated
   */
  function watchBoostSearch() {
    const observer = new MutationObserver(function(mutations) {
      // Check if Boost added new products or instant search results
      const hasNewProducts = mutations.some(mutation => {
        return Array.from(mutation.addedNodes).some(node => {
          if (node.nodeType === 1) { // Element node
            // Check for instant search container or product links
            if (node.classList) {
              if (node.classList.contains('boost-sd__instant-search-container') ||
                  node.classList.contains('boost-sd__suggestion-wrapper') ||
                  node.classList.contains('boost-sd__suggestion-queries-item-product') ||
                  node.classList.contains('boost-sd__product-item')) {
                return true;
              }
            }
            // Check if node contains product links
            if (node.querySelector) {
              return node.querySelector('[href*="/products/"], .boost-sd__instant-search-container, [class*="boost-sd__product"]');
            }
          }
          return false;
        });
      });

      if (hasNewProducts) {
        console.log('[Boost Yardage Search] New products detected, converting...');
        setTimeout(convertBoostSearchResults, 200); // Small delay to let Boost finish rendering
      }
    });

    // Observe the entire document for Boost adding products
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    console.log('[Boost Yardage Search] Monitoring for Boost search results...');
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      watchBoostSearch();
      convertBoostSearchResults();
    });
  } else {
    watchBoostSearch();
    convertBoostSearchResults();
  }

  // Also try converting after a short delay (in case Boost loads slowly)
  setTimeout(convertBoostSearchResults, 1000);
  setTimeout(convertBoostSearchResults, 2000);

})();
